/**
 * Version service for checking application updates
 * Compares local version with server version and notifies users of updates
 */

const VERSION_FILE_URL = '/ver.txt';

export interface VersionCheckResult {
    hasUpdate: boolean;
    currentVersion: string;
    latestVersion: string;
}

/**
 * Get the current application version, injected at build time from package.json.
 * This is the source of truth - the actual version the user loaded.
 */
export function getAppVersion(): string {
    // @ts-expect-error - VERSION is injected by Vite
    return typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '0.0.0';
}

/**
 * Compare two version strings using standard semver algorithm
 * Returns: 1 if v1 > v2, -1 if v1 < v2, 0 if equal
 */
export function compareVersions(v1: string, v2: string): number {
    const parts1 = v1.split('.').map(Number);
    const parts2 = v2.split('.').map(Number);
    const len = Math.max(parts1.length, parts2.length);

    for (let i = 0; i < len; i++) {
        const n1 = parts1[i] || 0;
        const n2 = parts2[i] || 0;
        if (n1 > n2) return 1;
        if (n1 < n2) return -1;
    }

    return 0;
}

/**
 * Fetch the latest version from server, with cache-busting.
 */
async function fetchLatestVersion(): Promise<string | null> {
    try {
        const url = `${VERSION_FILE_URL}?t=${Date.now()}`;
        const response = await fetch(url, {
            cache: 'no-store',
            headers: {
                'Cache-Control': 'no-cache',
                Pragma: 'no-cache',
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const version = await response.text();
        return version.trim();
    } catch (e) {
        console.warn('[VersionService] Unable to fetch latest version:', e);
        return null;
    }
}

/**
 * Check for updates.
 * Compares the BUILD version (what the user loaded) with the server version.
 * If the server version is less than the build version, the user has a newer
 * build (e.g. local dev), so no update is reported.
 */
export async function checkForUpdate(): Promise<VersionCheckResult | null> {
    const latestVersion = await fetchLatestVersion();

    if (!latestVersion) {
        return null;
    }

    const currentVersion = getAppVersion();
    return {
        hasUpdate: compareVersions(latestVersion, currentVersion) > 0,
        currentVersion,
        latestVersion,
    };
}
